import { EasyfyEventData, IBasketRow, IDataMapper, IEasyfyEvent, IProductData, ITrackableElement } from '@shared/easyfy.events/easyfy.eventdata';
import { EventNames } from '@shared/easyfy.events/easyfy.eventhandler';

class HayppGa4Product implements IProductData {
  Id: any;
  Ean: any;
  DisplayName: any;
  Brand: any;
  Categories: any[];
  Price: number;
  PriceExVat: number;
  CampaignCode: string;
  Flavour: string;
  Format: string;
  ProductType: string;
  DomainId: string;
  AbsoluteProductUrl: string;
  RelativeProductUrl: string;
  ProductImageUrl: string;
}

export const Ga4_CustomData_ShippingCode: string = 'ga4_shipping_code';

export interface HayppGa4Shipping {
  Code: string;
}

/* Exporter class */
export class Ga4DataMapper implements IDataMapper {
  MapEventData(event: IEasyfyEvent): any {
    let mappedEvent: any;

    switch (event.eventName.toLowerCase()) {
      case EventNames.ProductImpression: {
        mappedEvent = this.ViewItemListEvent(event);
        break;
      }
      case EventNames.ProductClick: {
        mappedEvent = this.SelectItemEvent(event);
        break;
      }
      case EventNames.ProductPageView: {
        mappedEvent = this.ViewItemEvent(event);
        break;
      }
      case EventNames.BannerImpression: {
        mappedEvent = this.ViewPromotionEvent(event);
        break;
      }
      case EventNames.BannerClick: {
        mappedEvent = this.SelectBannerPromotionEvent(event);
        break;
      }
      case EventNames.AddToCart: {
        mappedEvent = this.AddToCartEvent(event);
        break;
      }
      case EventNames.RemoveFromCart: {
        mappedEvent = this.RemoveFromCartEvent(event);
        break;
      }
      case EventNames.StartedCheckout: {
        mappedEvent = this.StartedCheckoutEvent(event);
        break;
      }
      case EventNames.ContinueToPayment: {
        mappedEvent = this.ContinueToPaymentEvent(event);
        break;
      }
      case EventNames.CompletedCheckout: {
        mappedEvent = this.CompletedCheckoutEvent(event);
        break;
      }
      case EventNames.ElementImpression: {
        if ((event.data as ITrackableElement).Type == 'mini-basket') {
          mappedEvent = this.ViewCartEvent(event);
        }
        break;
      }
      case EventNames.ElementClick: {
        if ((event.data as ITrackableElement).Type == 'consent-expansion') {
          mappedEvent = this.ExpandConsentEvent(event);
        }
        break;
      }
    }

    return mappedEvent;
  }

  ViewItemListEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifiers) throw new Error('Product identifiers missing');
    if (!event.data.salesUnitCodes) throw new Error('Product sales unit codes missing');
    if (event.data.salesUnitCodes.length != event.data.productIdentifiers.length) throw new Error('Product sales unit codes and identifiers count missmatch');
    const priceIds: string[] = [];
    const siteInfo = EasyfyEventData.SiteInfo();
    const viewedProductItems: any[] = [];
    const promotedProductEvents: any[] = [];

    for (let i = 0; i < event.data.productIdentifiers.length; i++) {
      priceIds.push(event.data.productIdentifiers[i] + '-' + event.data.salesUnitCodes[i]);
    }

    const eventProducts = EasyfyEventData.Products<HayppGa4Product>(event.data.productIdentifiers);
    const eventPrices = EasyfyEventData.ProductSalesUnitPrices(priceIds);

    for (let i = 0; i < eventProducts.length; i++) {
      const product = eventProducts[i];
      const salesUnitPrice = eventPrices[i];

      const index = eventProducts.indexOf(product);
      const productListIndex = EasyfyEventData.ProductIndexInList(event.data.listIds[index], product.Id) + 1;

      let eventData = {
        discount: null,
        variant: null,
        coupon: null,
        price: salesUnitPrice?.priceExVat,
        index: productListIndex,
        item_list_name: event.data.listIds[index],
        item_variant: null,
        item_id: product.Id,
        item_name: product.DisplayName,
        item_brand: product.Brand,
        item_category: product.Categories.length > 0 ? product.Categories[0] : null,
        item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
        item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
        item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
        item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
        quantity: 1,
        cd_flavour: product.Flavour,
        cd_format: product.Format,
        cd_product_type: product.ProductType,
        cd_packsize: salesUnitPrice?.packSize,
      };

      viewedProductItems.push(eventData);

      if (event.data.promotedProducts[i]) {
        promotedProductEvents.push(eventData);
      }
    }

    const gaEvent = {
      event: 'view_item_list',
      ecommerce: {
        currency: siteInfo.currencyCode,
        items: viewedProductItems,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    // if any product is "promoted" - also send separate view_promotion event
    if (promotedProductEvents.length > 0) {
      const gaPromotedEvent = {
        event: 'view_promotion',
        ecommerce: {
          currency: siteInfo.currencyCode,
          items: promotedProductEvents,
          cd_timestamp: this.GetTimeStamp(),
        },
      };

      return [gaEvent, gaPromotedEvent];
    } else {
      return gaEvent;
    }
  }

  SelectItemEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifier) throw new Error('Product identifier missing');

    const product = EasyfyEventData.Product<HayppGa4Product>(event.data.productIdentifier);
    const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + '-' + event.data.salesUnitCode);
    const productListIndex = EasyfyEventData.ProductIndexInList(event.data.listName, product.Id);
    const siteInfo = EasyfyEventData.SiteInfo();
    const ga4item: any[] = [];

    ga4item.push({
      discount: null,
      variant: null,
      coupon: null,
      price: salesUnitPrice?.priceExVat,
      index: productListIndex + 1,
      item_list_name: event.data.listName,
      item_variant: null,
      item_id: product.Id,
      item_name: product.DisplayName,
      item_brand: product.Brand,
      item_category: product.Categories.length > 0 ? product.Categories[0] : null,
      item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
      item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
      item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
      item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
      quantity: 1,
      cd_flavour: product.Flavour,
      cd_format: product.Format,
      cd_product_type: product.ProductType,
      cd_packsize: salesUnitPrice?.packSize,
    });

    const gaEvent = {
      event: 'select_item',
      ecommerce: {
        currency: siteInfo.currencyCode,
        items: ga4item,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    // if product is "promoted" - also send separate select_promotion event
    if (event.data.isPromotedProduct) {
      const promotedClickEvent = {
        event: 'select_promotion',
        ecommerce: {
          currency: siteInfo.currencyCode,
          items: ga4item,
          cd_timestamp: this.GetTimeStamp(),
        },
      };

      return [gaEvent, promotedClickEvent];
    } else {
      return gaEvent;
    }
  }

  ViewItemEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifier) throw new Error('Product identifier missing');

    const product = EasyfyEventData.Product<HayppGa4Product>(event.data.productIdentifier);
    const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + '-' + event.data.salesUnitCode);

    const siteInfo = EasyfyEventData.SiteInfo();
    const ga4items: any[] = [];

    ga4items.push({
      discount: null,
      variant: null,
      coupon: null,
      price: salesUnitPrice?.priceExVat,
      index: null,
      item_list_id: null,
      item_list_name: null,
      item_variant: null,
      item_id: product.Id,
      item_name: product.DisplayName,
      item_brand: product.Brand,
      item_category: product.Categories.length > 0 ? product.Categories[0] : null,
      item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
      item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
      item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
      item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
      quantity: 1,
      cd_flavour: product.Flavour,
      cd_format: product.Format,
      cd_product_type: product.ProductType,
      cd_packsize: salesUnitPrice?.packSize,
    });

    const gaEvent = {
      event: 'view_item',
      ecommerce: {
        currency: siteInfo.currencyCode,
        value: salesUnitPrice?.priceExVat,
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }
  ViewPromotionEvent(event: IEasyfyEvent): any {
    if (!event.data.bannerIdentifier) throw new Error('Banner identifier missing');

    const banner = EasyfyEventData.Banner(event.data.bannerIdentifier);
    const ga4items: any[] = [];

    ga4items.push({
      promotion_id: banner.Id,
      promotion_name: banner.DisplayName,
      quantity: 1,
      cd_linkUrl: banner.LinkUrl,
      cd_pageUrl: banner.PageUrl,
      creative_name: null,
      creative_slot: null,
      discount: null,
      coupon: null,
      price: null,
      item_list_name: null,
      item_variant: null,
      item_id: null,
      item_name: null,
      item_brand: null,
      item_category: null,
      item_category2: null,
      item_category3: null,
      item_category4: null,
      item_category5: null,
    });

    const gaEvent = {
      event: 'view_promotion',
      ecommerce: {
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  SelectBannerPromotionEvent(event: IEasyfyEvent): any {
    if (!event.data.bannerIdentifier) throw new Error('Banner identifier missing');

    const banner = EasyfyEventData.Banner(event.data.bannerIdentifier);
    const ga4items: any[] = [];

    ga4items.push({
      promotion_id: banner.Id,
      promotion_name: banner.DisplayName,
      quantity: 1,
      cd_linkUrl: banner.LinkUrl,
      cd_pageUrl: banner.PageUrl,
      creative_name: null,
      creative_slot: null,
      discount: null,
      coupon: null,
      price: null,
      item_list_name: null,
      item_variant: null,
      item_id: null,
      item_name: null,
      item_brand: null,
      item_category: null,
      item_category2: null,
      item_category3: null,
      item_category4: null,
      item_category5: null,
    });

    const gaEvent = {
      event: 'select_promotion',
      ecommerce: {
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  AddToCartEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifier) throw new Error('Product identifier missing');

    const product = EasyfyEventData.Product<HayppGa4Product>(event.data.productIdentifier);
    const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + '-' + event.data.salesUnitCode);

    const siteInfo = EasyfyEventData.SiteInfo();
    const ga4items: any[] = [];

    let listId = this.ListId(event.data.listName, event.data.listId);

    ga4items.push({
      discount: null,
      variant: null,
      coupon: null,
      price: salesUnitPrice?.priceExVat,
      index: null,
      item_list_name: event.data.listName,
      item_list_id: listId,
      item_variant: null,
      item_id: product.Id,
      item_name: product.DisplayName,
      item_brand: product.Brand,
      item_category: product.Categories.length > 0 ? product.Categories[0] : null,
      item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
      item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
      item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
      item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
      quantity: event.data.quantity,
      cd_flavour: product.Flavour,
      cd_format: product.Format,
      cd_product_type: product.ProductType,
      cd_packsize: salesUnitPrice?.packSize,
      cd_add: event.data.listName,
    });

    const gaEvent = {
      event: 'add_to_cart',
      ecommerce: {
        currency: siteInfo.currencyCode,
        value: salesUnitPrice?.priceExVat * event.data.quantity,
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  RemoveFromCartEvent(event: IEasyfyEvent): any {
    if (!event.data.productIdentifier) throw new Error('Product identifier missing');

    const product = EasyfyEventData.Product<HayppGa4Product>(event.data.productIdentifier);
    const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(event.data.productIdentifier + '-' + event.data.salesUnitCode);
    const siteInfo = EasyfyEventData.SiteInfo();
    const ga4items: any[] = [];

    ga4items.push({
      discount: null,
      variant: null,
      coupon: null,
      price: salesUnitPrice?.priceExVat,
      index: null,
      item_list_name: event.data.listName,
      item_variant: null,
      item_id: product.Id,
      item_name: product.DisplayName,
      item_brand: product.Brand,
      item_category: product.Categories.length > 0 ? product.Categories[0] : null,
      item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
      item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
      item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
      item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
      quantity: event.data.quantity,
      cd_flavour: product.Flavour,
      cd_format: product.Format,
      cd_product_type: product.ProductType,
      cd_packsize: salesUnitPrice?.packSize,
    });

    const gaEvent = {
      event: 'remove_from_cart',
      ecommerce: {
        currency: siteInfo.currencyCode,
        value: salesUnitPrice?.priceExVat * event.data.quantity,
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  StartedCheckoutEvent(event: IEasyfyEvent): any {
    if (!event.data.checkoutProducts || event.data.checkoutProducts.length == 0) throw new Error('No products found');
    const ga4items: any[] = [];
    const siteInfo = EasyfyEventData.SiteInfo();
    let value: number = 0;

    event.data.checkoutProducts.forEach((checkoutProduct) => {
      const product = EasyfyEventData.Product<HayppGa4Product>(checkoutProduct.productIdentifier);
      const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(checkoutProduct.productIdentifier + '-' + checkoutProduct.salesUnitCode);

      ga4items.push({
        discount: null,
        variant: null,
        coupon: null,
        price: salesUnitPrice?.priceExVat,
        index: null,
        item_list_name: event.data.listName,
        item_variant: null,
        item_id: product.Id,
        item_name: product.DisplayName,
        item_brand: product.Brand,
        item_category: product.Categories.length > 0 ? product.Categories[0] : null,
        item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
        item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
        item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
        item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
        quantity: checkoutProduct.qty,
        cd_flavour: product.Flavour,
        cd_format: product.Format,
        cd_product_type: product.ProductType,
        cd_packsize: salesUnitPrice?.packSize,
      });

      value += salesUnitPrice?.priceExVat * checkoutProduct.qty;
    });

    const gaEvent = {
      event: 'begin_checkout',
      ecommerce: {
        currency: siteInfo.currencyCode,
        value: value,
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  ContinueToPaymentEvent(event: IEasyfyEvent): any {
    if (!event.data.checkoutProducts || event.data.checkoutProducts.length == 0) {
      throw new Error('No products found');
    }
    const ga4items: any[] = [];
    const siteInfo = EasyfyEventData.SiteInfo();
    const shippingTier = EasyfyEventData.GetCustomData(Ga4_CustomData_ShippingCode);

    event.data.checkoutProducts.forEach((checkoutProduct) => {
      const product = EasyfyEventData.Product<HayppGa4Product>(checkoutProduct.productIdentifier);
      const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(checkoutProduct.productIdentifier + '-' + checkoutProduct.salesUnitCode);

      ga4items.push({
        discount: null,
        variant: null,
        coupon: null,
        price: salesUnitPrice?.priceExVat,
        index: null,
        item_list_name: null,
        item_variant: null,
        item_id: product.Id,
        item_name: product.DisplayName,
        item_brand: product.Brand,
        item_category: product.Categories.length > 0 ? product.Categories[0] : null,
        item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
        item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
        item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
        item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
        quantity: checkoutProduct.qty,
        cd_campaign_code: product.CampaignCode,
        cd_flavour: product.Flavour,
        cd_format: product.Format,
        cd_product_type: product.ProductType,
        cd_packsize: salesUnitPrice?.packSize,
      });
    });

    const gaEvent = {
      event: 'add_shipping_info',
      ecommerce: {
        currency: siteInfo.currencyCode,
        items: ga4items,
        shipping_tier: shippingTier?.Code ?? 'Not-set',
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    const gaEventContinuePayment = {
      ...gaEvent,
      event: 'continue_to_payment',
    };

    return [gaEvent, gaEventContinuePayment]; // multiple gaEvent;
  }

  CompletedCheckoutEvent(event: IEasyfyEvent): any {
    if (!event.data.checkoutCompletedProducts || event.data.checkoutCompletedProducts.length == 0) throw new Error('No products found');

    const userConsents = event.data.userConsents;
    let trueConsents: string[] = [];
    if (userConsents) trueConsents = Object.keys(userConsents).filter((key) => userConsents[key] === true);

    const ga4items: any[] = [];
    const siteInfo = EasyfyEventData.SiteInfo();

    event.data.checkoutCompletedProducts.forEach((checkoutProduct: IBasketRow) => {
      const product = EasyfyEventData.Product<HayppGa4Product>(checkoutProduct.productIdentifier);
      const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(checkoutProduct.productIdentifier + '-' + checkoutProduct.salesUnitCode);
      const purchaseFlow = checkoutProduct.purchaseFlow;

      ga4items.push({
        discount: null,
        variant: null,
        coupon: null,
        price: salesUnitPrice?.priceExVat,
        index: null,
        item_list_name: event.data.listName,
        item_variant: null,
        item_id: product.Id,
        item_name: product.DisplayName,
        item_brand: product.Brand,
        item_category: product.Categories.length > 0 ? product.Categories[0] : null,
        item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
        item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
        item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
        item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
        quantity: checkoutProduct.qty,
        cd_flavour: product.Flavour,
        cd_format: product.Format,
        cd_product_type: product.ProductType,
        cd_packsize: salesUnitPrice?.packSize,
        cd_purchase_flow: purchaseFlow,
      });
    });

    const gaEvent = {
      event: 'purchase',
      ecommerce: {
        transaction_id: event.data.completedCheckoutId,
        value: event.data.totalPriceExVat,
        tax: event.data.totalVatAmount,
        shipping: event.data.shipping,
        currency: siteInfo.currencyCode,
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
        email_consent: trueConsents,
      },
    };

    return gaEvent;
  }

  ViewCartEvent(event: IEasyfyEvent): any {
    const ga4items: any[] = [];
    const siteInfo = EasyfyEventData.SiteInfo();
    const basket = EasyfyEventData.Basket();
    let value: number = 0;

    if (basket && basket.rows) {
      basket.rows.forEach((row) => {
        const product = EasyfyEventData.Product<HayppGa4Product>(row.productIdentifier);
        const salesUnitPrice = EasyfyEventData.ProductSalesUnitPrice(row.productIdentifier + '-' + row.salesUnitCode);

        ga4items.push({
          discount: null,
          variant: null,
          coupon: null,
          price: salesUnitPrice?.priceExVat,
          index: null,
          item_list_name: event.data.listName,
          item_variant: null,
          item_id: product.Id,
          item_name: product.DisplayName,
          item_brand: product.Brand,
          item_category: product.Categories.length > 0 ? product.Categories[0] : null,
          item_category2: product.Categories.length > 1 ? product.Categories[1] : null,
          item_category3: product.Categories.length > 2 ? product.Categories[2] : null,
          item_category4: product.Categories.length > 3 ? product.Categories[3] : null,
          item_category5: product.Categories.length > 4 ? product.Categories[4] : null,
          quantity: row.qty,
          cd_flavour: product.Flavour,
          cd_format: product.Format,
          cd_product_type: product.ProductType,
          cd_packsize: salesUnitPrice?.packSize,
        });

        value += salesUnitPrice?.priceExVat * row.qty;
      });
    }

    const gaEvent = {
      event: 'view_cart',
      ecommerce: {
        value: value,
        currency: siteInfo.currencyCode,
        items: ga4items,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  ExpandConsentEvent(event: IEasyfyEvent): any {
    const data = event.data as ITrackableElement | undefined;
    const interactionSection: string = (data?.Properties.get('consent-type') ?? '').toUpperCase();

    const gaEvent = {
      event: 'consent_expansion',
      ecommerce: {
        interaction_section: interactionSection,
        cd_timestamp: this.GetTimeStamp(),
      },
    };

    return gaEvent;
  }

  GetTimeStamp(): string {
    return new Date().toISOString();
  }

  ListId(listName: string, listId: string | null | undefined): string {
    if (!listId || listId.trim().length === 0) {
      listId = listName;
    }
    return listId;
  }
}
